// api.ts — TanStack Query hooks for the PRESCRIPTIONS module (server state).
// Mock mode: hooks hit the /rx/* MSW handlers (port of the prototype's RX_*
// reducer). Real-API mode: paths resolve to /api/prescriptions/ DRF routes;
// dispense posts the prescription's draft StockOutward, and statuses without
// a backend transition reject with the standard NOT_WIRED toast.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiErrorDetail, apiFetch, apiFetchList } from '@/api/client';
import { toastBus } from '@/foundation/toastBus';
import { resolvePath, useRealApi } from '@/api/paths';
import { unwrapPage } from '@/api/adapters';
import { patientToUi, prescriptionToRx, type PatientDto } from '@/api/rxAdapters';
import { useStore } from '@/store/store';
import { parseDate } from '@/domain/dates';
import type { RxPatient, RxPrescription, RxStatus, SpineState } from '@/domain/types';

export const rxKeys = {
  all: () => ['rx', 'prescriptions'] as const,
  detail: (id: string) => ['rx', 'prescriptions', id] as const,
  patients: (q: string) => ['rx', 'patients', q] as const,
  spine: () => ['rx', 'spine'] as const,
};

const RX_INVALIDATE = [rxKeys.all(), ['rx'], ['stocks'], ['inventory']] as const;

const NOT_WIRED = 'This action is not available on the live API yet (mock mode only)';

/** dd/mm/yyyy (UI) → yyyy-mm-dd (DRF DateField); passes through ISO input. */
function isoDate(uk: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(uk)) return uk;
  const d = parseDate(uk);
  return Number.isNaN(d.getTime()) ? uk : d.toISOString().slice(0, 10);
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function usePrescriptions() {
  const { store } = useStore();
  const resolved = resolvePath('/rx/prescriptions');
  // Narrow to the active store (real API only) so the queue matches the topbar — like alerts.
  const storeId =
    useRealApi && store !== 'ALL' && /^[0-9a-f-]{8,}/i.test(store) ? store : undefined;
  return useQuery({
    queryKey: [...rxKeys.all(), resolved?.path ?? 'noop', storeId ?? 'all'],
    enabled: resolved !== null,
    queryFn: async (): Promise<RxPrescription[]> => {
      if (!resolved) return [];
      const data = await apiFetchList<unknown>(
        resolved.path,
        storeId ? { query: { store_id: storeId } } : undefined,
      );
      const rows = resolved.paginated ? unwrapPage<unknown>(data) : (data as unknown[]);
      return useRealApi ? rows.map(prescriptionToRx) : (rows as RxPrescription[]);
    },
  });
}

export function usePrescription(id: string | undefined) {
  return useQuery({
    queryKey: rxKeys.detail(id ?? 'none'),
    enabled: !!id,
    queryFn: async (): Promise<RxPrescription> => {
      const path = useRealApi ? `/api/prescriptions/${id}/` : `/rx/prescriptions/${id}`;
      const raw = await apiFetch<unknown>(path);
      return useRealApi ? prescriptionToRx(raw) : (raw as RxPrescription);
    },
  });
}

export function useRxPatients(q: string, dob = '') {
  return useQuery({
    queryKey: [...rxKeys.patients(q), dob],
    // Searchable by name/NHS/token (>=2 chars) OR a full date of birth (YYYY-MM-DD).
    enabled: q.length >= 2 || dob.length === 10,
    queryFn: async (): Promise<RxPatient[]> => {
      const query: Record<string, string> = {};
      if (q) query.q = q;
      if (dob) query.dob = dob;
      if (!useRealApi) {
        return apiFetch<RxPatient[]>('/rx/patients', { query });
      }
      const data = await apiFetch<{ results: PatientDto[] }>('/api/prescriptions/patients/', {
        query,
      });
      return (data.results ?? []).map(patientToUi);
    },
  });
}

/** Spine connection panel. The live EPS integration is a Tier-4 feed; in
 *  real-API mode the panel shows the configured stub state. */
export function useSpine() {
  return useQuery({
    queryKey: rxKeys.spine(),
    queryFn: async (): Promise<SpineState & { manualMode: boolean }> => {
      if (!useRealApi) {
        return apiFetch<SpineState & { manualMode: boolean }>('/rx/spine');
      }
      return {
        status: 'Connected',
        tokenValidUntil: '—',
        autoRenew: true,
        lastSync: '—',
        env: 'EPS Spine 2 · Integration (stub)',
        appId: 'PIQ-EPS-441902',
        manualMode: false,
      };
    },
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

interface RealAction {
  path: string;
  method?: 'POST' | 'PATCH';
  body?: object;
  idempotent?: boolean;
}

function runReal(spec: RealAction | null): Promise<unknown> {
  if (!spec) return Promise.reject(new Error(NOT_WIRED));
  return apiFetch<unknown>(spec.path, {
    method: spec.method ?? 'POST',
    body: spec.body ?? {},
    headers: spec.idempotent ? { 'Idempotency-Key': crypto.randomUUID() } : undefined,
  });
}

/** POST to an /rx action path (mock) or the mapped DRF action (real). */
function useRxAction<TBody extends object>(
  mockPath: (body: TBody & { id: string }) => string,
  real?: (body: TBody & { id: string }) => RealAction | null,
  mockBody?: (body: TBody & { id: string }) => object,
  onError?: (error: unknown) => void,
) {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: (body: TBody & { id: string }) => {
      if (useRealApi) return runReal(real?.(body) ?? null);
      return apiFetch<unknown>(mockPath(body), {
        method: 'POST',
        body: { ...(mockBody ? mockBody(body) : body), who: me, role },
      });
    },
    onSuccess: () =>
      RX_INVALIDATE.forEach((key) => qc.invalidateQueries({ queryKey: key as never })),
    // A hook-level onError replaces the global error toast (queryClient.ts) for this action.
    ...(onError ? { onError } : {}),
  });
}

export const useAllocateRx = () =>
  useRxAction<object>(
    ({ id }) => `/rx/prescriptions/${id}/allocate`,
    ({ id }) => ({ path: `/api/prescriptions/${id}/allocate/` }),
  );

export interface ClinicalCheckInput {
  decision: 'approved' | 'query' | 'rejected';
  allergyChecked?: boolean;
  interactionsChecked?: boolean;
  dosageChecked?: boolean;
  legalityChecked?: boolean;
  cdScheduleChecked?: boolean | null;
  revalidationMode?: 'full' | 'risk_based';
  notes?: string;
  queryReason?: string;
  rejectionReason?: string;
}

/** Phase-1 clinical-check gate — approve / query / reject (PH only). */
export const useClinicalCheckRx = () =>
  useRxAction<ClinicalCheckInput>(
    ({ id }) => `/rx/prescriptions/${id}/clinical_check`,
    ({ id, decision, ...rest }) => ({
      path: `/api/prescriptions/${id}/clinical_check/`,
      body: {
        decision,
        allergy_checked: rest.allergyChecked ?? false,
        interactions_checked: rest.interactionsChecked ?? false,
        dosage_checked: rest.dosageChecked ?? false,
        legality_checked: rest.legalityChecked ?? false,
        cd_schedule_checked: rest.cdScheduleChecked ?? null,
        revalidation_mode: rest.revalidationMode ?? 'full',
        notes: rest.notes ?? '',
        query_reason: rest.queryReason ?? '',
        rejection_reason: rest.rejectionReason ?? '',
      },
    }),
  );

export interface ResolveQueryInput {
  resolutionNote: string;
}

/** Resolve a raised clinical query and return the Rx for re-check (PH only). */
export const useResolveRxQuery = () =>
  useRxAction<ResolveQueryInput>(
    ({ id }) => `/rx/prescriptions/${id}/resolve_query`,
    ({ id, resolutionNote }) => ({
      path: `/api/prescriptions/${id}/resolve_query/`,
      body: { resolution_note: resolutionNote },
    }),
  );

/** Send a pre-picking Rx back to clinical check — releases the reserved stock and clears
 *  the clinical check so a pharmacist can re-check (PH only). Real-API only. */
export const useRevertToClinicalCheck = () =>
  useRxAction<{ reason?: string }>(
    ({ id }) => `/rx/prescriptions/${id}/revert_to_clinical_check`,
    ({ id, reason }) => ({
      path: `/api/prescriptions/${id}/revert_to_clinical_check/`,
      body: { reason: reason ?? '' },
    }),
  );

/** Cancel a script that has not been dispensed (D1): releases its stock, voids the dispense job,
 *  frees the basket and closes owings. A reason is required; PH / admin only. Real-API only. */
export const useCancelPrescription = () =>
  useRxAction<{ reason: string }>(
    ({ id }) => `/rx/prescriptions/${id}/cancel`,
    ({ id, reason }) => ({ path: `/api/prescriptions/${id}/cancel/`, body: { reason } }),
  );

/** Reveal ONE script's full NHS number with a reason — PH/admin, audited (M3-D2). Not cached:
 *  the full number lives only in the component that asked for it. Real-API only. */
export const useRevealNhsNumber = () =>
  useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiFetch<{ nhs_number: string }>(`/api/prescriptions/${id}/reveal_nhs_number/`, {
        method: 'POST',
        body: { reason },
      }),
  });

/** Bin-directed picking list (Phase 3 — §5 step 4). Real-API only. */
export function usePickingList(id: string | undefined) {
  return useQuery({
    queryKey: ['rx', 'prescriptions', id ?? 'none', 'picking-list'],
    enabled: !!id,
    queryFn: async () => {
      if (!id) return [];
      const path = useRealApi
        ? `/api/prescriptions/${id}/picking_list/`
        : `/rx/prescriptions/${id}/picking_list`;
      const data = await apiFetch<{ items: unknown[] }>(path);
      return data.items ?? [];
    },
  });
}

/** A batch available to dispense for a line (one of possibly many under a VMPP family). */
export interface AvailableBatch {
  batch_id: string;
  batch_number: string;
  expiry_date: string | null;
  qty_free: number;
  bin_location: string;
  units_per_pack: number;
  /** Within the store's near-expiry window (use-it-or-lose-it). */
  near_expiry?: boolean;
  /** Free units sitting in already-opened packs of this batch (finish these first). */
  open_free_units?: number;
  /** The single wastage-first FEFO pick for the whole line — the "best way to dispense" batch. */
  recommended?: boolean;
}
/** One actual product with its free batches — a VMP/VMPP line yields many (across pack sizes and
 *  makers), an AMPP line yields one. `pack_size`/`pack_qty` + `manufacturer` let the picker group
 *  a generic family by PACK SIZE → MAKER. */
export interface AvailableProduct {
  snapshot_key: string;
  product_name: string;
  manufacturer: string;
  pack_size: string;
  pack_qty: number | null;
  total_free: number;
  /** C+D catalogue pack cost (GBP, Drug Tariff else trade); null when unpriced. Context only. */
  pack_cost?: number | null;
  batches: AvailableBatch[];
}
export interface LineAvailableBatches {
  line_id: string;
  level: string;
  sku: string;
  /** Prescribed amount; interpreted as unit doses when `qty_in_units` (VMP/AMP) else packs. */
  qty_prescribed: number;
  qty_in_units: boolean;
  /** The wastage-first FEFO batch to dispense from, and a short "why" (expiry / open pack / FEFO). */
  recommended_batch_id?: string | null;
  recommended_reason?: string;
  expiry_alert_days?: number;
  products: AvailableProduct[];
}

/** Family-aware available stock for a line. VMPP lines return every pack in the virtual-pack
 *  family (grouped by product); AMPP/C+D lines return the single product. Real API only. */
export function useLineAvailableBatches(
  prescriptionId: string | undefined,
  lineId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['rx', 'prescriptions', prescriptionId ?? 'none', 'available-batches', lineId ?? ''],
    enabled: !!prescriptionId && !!lineId && useRealApi && enabled,
    queryFn: async () => {
      const raw = await apiFetch<LineAvailableBatches>(
        `/api/prescriptions/${prescriptionId}/available-batches/`,
        { query: { line_id: lineId! } },
      );
      return raw;
    },
  });
}

export const useStartPicking = () =>
  useRxAction<object>(
    ({ id }) => `/rx/prescriptions/${id}/start_picking`,
    ({ id }) => ({ path: `/api/prescriptions/${id}/start_picking/` }),
  );

export interface PickScanInput {
  lineId: string;
  productCode?: string;
  batchNumber?: string;
  overrideReason?: string;
}

export const usePickScan = () =>
  useRxAction<PickScanInput>(
    ({ id }) => `/rx/prescriptions/${id}/pick_scan`,
    ({ id, lineId, productCode, batchNumber, overrideReason }) => ({
      path: `/api/prescriptions/${id}/pick_scan/`,
      body: {
        line_id: lineId,
        product_code: productCode ?? '',
        batch_number: batchNumber ?? '',
        override_reason: overrideReason ?? '',
      },
    }),
  );

export const useCompletePicking = () =>
  useRxAction<object>(
    ({ id }) => `/rx/prescriptions/${id}/complete_picking`,
    ({ id }) => ({ path: `/api/prescriptions/${id}/complete_picking/` }),
  );

export interface PickPartPackInput {
  lineId: string;
  qty: number;
  /** Full pack size — required only when opening a new pack (no reusable open pack). */
  originalQty?: number;
}

/** Open/use a part pack for a line during picking (fractional dispense, §11). */
export const usePickPartPack = () =>
  useRxAction<PickPartPackInput>(
    ({ id }) => `/rx/prescriptions/${id}/pick_part_pack`,
    ({ id, lineId, qty, originalQty }) => ({
      path: `/api/prescriptions/${id}/pick_part_pack/`,
      body: { line_id: lineId, qty, original_qty: originalQty ?? null },
    }),
  );

export interface AccuracyCheckInput {
  passed: boolean;
  notes?: string;
  /** Required when the checker picked the script themselves (D3). */
  selfCheckReason?: string;
}

/** The backend refused a self-check that had no reason (D3) — ask for one and resend. */
export function isSelfCheckReasonRequired(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    e.status === 409 &&
    (e.body as { code?: string } | undefined)?.code === 'SELF_CHECK_REASON_REQUIRED'
  );
}

/** Pharmacist accuracy-check gate (Phase 4 — PH only). */
export const useAccuracyCheck = () =>
  useRxAction<AccuracyCheckInput>(
    ({ id }) => `/rx/prescriptions/${id}/accuracy_check`,
    ({ id, passed, notes, selfCheckReason }) => ({
      path: `/api/prescriptions/${id}/accuracy_check/`,
      body: {
        passed,
        notes: notes ?? '',
        ...(selfCheckReason ? { self_check_reason: selfCheckReason } : {}),
      },
    }),
    undefined,
    // The self-check refusal opens a reason prompt on the page — not an error to toast. Every
    // other failure toasts exactly as the global handler would.
    (e) => {
      if (!isSelfCheckReasonRequired(e))
        toastBus.emit(apiErrorDetail(e, 'Request failed'), 'error');
    },
  );

export interface DispenseBag {
  id: string;
  bag_type: 'ambient' | 'cold_chain' | 'cd';
  status: 'pending' | 'placed' | 'collected' | 'cancelled';
  /** Scannable bag QR payload (BAG:<id>), minted at bagging. */
  barcode?: string;
  bagged_at?: string | null;
  dispense_location_id: string | null;
  location_code: string | null;
  location_type: string | null;
  placed_at: string | null;
}

/** The prescription's dispense bags + placement/location state (DL-2/DL-3). */
export function useDispenseBags(id: string | undefined) {
  return useQuery({
    queryKey: ['rx', 'prescriptions', id ?? 'none', 'bags'],
    enabled: !!id,
    queryFn: async (): Promise<DispenseBag[]> => {
      if (!id) return [];
      const path = useRealApi ? `/api/prescriptions/${id}/bags/` : `/rx/prescriptions/${id}/bags`;
      const data = await apiFetch<{ bags: DispenseBag[] }>(path);
      return data.bags ?? [];
    },
  });
}

export interface PlaceBagInput {
  bagType: string;
  barcode?: string;
  locationId?: string;
}

/** Place a bag at a scanned dispense location (CD/fridge enforced) (DL-2). */
export const usePlaceBag = () =>
  useRxAction<PlaceBagInput>(
    ({ id }) => `/rx/prescriptions/${id}/place_bag`,
    ({ id, bagType, barcode, locationId }) => ({
      path: `/api/prescriptions/${id}/place_bag/`,
      body: { bag_type: bagType, barcode: barcode ?? '', location_id: locationId ?? null },
    }),
  );

export interface CollectBagInput {
  bagType: string;
  signature?: string;
  cdWitnessId?: string;
  collectedByPatient?: boolean;
}

/** Collect one bag; response carries the cross-location alert + auto-close (DL-3). */
export const useCollectBag = () =>
  useRxAction<CollectBagInput>(
    ({ id }) => `/rx/prescriptions/${id}/collect_bag`,
    ({ id, bagType, signature, cdWitnessId, collectedByPatient }) => ({
      path: `/api/prescriptions/${id}/collect_bag/`,
      body: {
        bag_type: bagType,
        signature: signature ?? '',
        cd_witness_id: cdWitnessId ?? null,
        collected_by_patient: collectedByPatient ?? true,
      },
    }),
  );

/** Return an uncollected bag to stock (DL-5 — PH/IM). `returnToStock` recounts an
 *  already-dispensed (posted) bag's units back into inventory. */
export const useCancelBag = () =>
  useRxAction<{ bagType: string; reason?: string; returnToStock?: boolean }>(
    ({ id }) => `/rx/prescriptions/${id}/cancel_bag`,
    ({ id, bagType, reason, returnToStock }) => ({
      path: `/api/prescriptions/${id}/cancel_bag/`,
      body: {
        bag_type: bagType,
        reason: reason ?? '',
        return_to_stock: returnToStock ?? true,
      },
    }),
  );

/** Assign the checked tray to a dispense bench (Phase 5). */
export const useAssignBench = () =>
  useRxAction<{ benchLocation: string }>(
    ({ id }) => `/rx/prescriptions/${id}/assign_bench`,
    ({ id, benchLocation }) => ({
      path: `/api/prescriptions/${id}/assign_bench/`,
      body: { bench_location: benchLocation },
    }),
  );

export interface CollectInput {
  collectedByPatient?: boolean;
  signature?: string;
  representativeName?: string;
  representativeRelationship?: string;
  cdWitnessId?: string;
  counsellingOffered?: boolean;
}

/** Patient collection with signature + CD witness (Phase 5 — PH / Counter). */
export const useCollectPrescription = () =>
  useRxAction<CollectInput>(
    ({ id }) => `/rx/prescriptions/${id}/collect`,
    ({ id, ...rest }) => ({
      path: `/api/prescriptions/${id}/collect/`,
      body: {
        collected_by_patient: rest.collectedByPatient ?? true,
        signature: rest.signature ?? '',
        representative_name: rest.representativeName ?? '',
        representative_relationship: rest.representativeRelationship ?? '',
        cd_witness_id: rest.cdWitnessId ?? null,
        counselling_offered: rest.counsellingOffered ?? false,
      },
    }),
  );

/** Post the ledger, notify EPS, close the Rx and release the tray (Phase 5 — PH). */
export const useDispenseAndClose = () =>
  useRxAction<object>(
    ({ id }) => `/rx/prescriptions/${id}/dispense_and_close`,
    ({ id }) => ({ path: `/api/prescriptions/${id}/dispense_and_close/` }),
  );

export interface ScanLineInput {
  lineId: string;
  result?: 'Match' | 'Mismatch';
  barcode?: string;
}

export const useScanRxLine = () =>
  useRxAction<ScanLineInput>(
    ({ id }) => `/rx/prescriptions/${id}/scan_line`,
    ({ id, lineId, result, barcode }) => ({
      path: `/api/prescriptions/${id}/scan_line/`,
      body: {
        line_id: lineId,
        ...(barcode ? { barcode } : {}),
        ...(result ? { result: result.toLowerCase() } : {}),
      },
    }),
    ({ lineId, result, barcode }) => ({
      line_id: lineId,
      ...(barcode ? { barcode } : {}),
      ...(result ? { result } : {}),
    }),
  );

export interface ResolveScanInput {
  lineId: string;
  reason: string;
  witness: string;
  batch?: string;
}

export const useResolveRxScan = () =>
  useRxAction<ResolveScanInput>(
    ({ id }) => `/rx/prescriptions/${id}/resolve_scan`,
    ({ id, lineId, reason, witness }) => ({
      path: `/api/prescriptions/${id}/resolve_scan/`,
      body: { line_id: lineId, reason, witness },
    }),
    ({ lineId, reason, witness, batch }) => ({ line_id: lineId, reason, witness, batch }),
  );

export interface OverrideLineInput {
  lineId: string;
  /** Mock batch id (e.g. B-AT22). */
  batch?: string;
  /** Real BatchStock uuid. */
  batchStockId?: string;
  reason: string;
}

export const useOverrideRxLine = () =>
  useRxAction<OverrideLineInput>(
    ({ id }) => `/rx/prescriptions/${id}/override_line`,
    ({ id, lineId, batchStockId, reason }) =>
      batchStockId
        ? {
            path: `/api/prescriptions/${id}/override_line/`,
            body: { line_id: lineId, batch_stock_id: batchStockId, reason },
          }
        : null,
    ({ lineId, batch, reason }) => ({ line_id: lineId, batch, reason }),
  );

export interface RxStatusInput {
  status: RxStatus;
  note?: string;
}

/** Manual status footer. Only mock mode supports the full prototype status
 *  set; the backend's lifecycle transitions happen via allocate/dispense. */
export const useSetRxStatus = () =>
  useRxAction<RxStatusInput>(({ id }) => `/rx/prescriptions/${id}/status`);

export interface DispenseRxInput {
  /** Draft StockOutward uuid backing the dispense (real mode). */
  outwardId?: string | null;
  witness?: string;
}

export const useDispenseRx = () =>
  useRxAction<DispenseRxInput>(
    ({ id }) => `/rx/prescriptions/${id}/dispense`,
    ({ outwardId }) =>
      outwardId
        ? {
            path: `/api/stocks/outward/${outwardId}/post_entry/`,
            body: { override_reason: 'Dispensed via Prescription Manager (basket bypassed)' },
            idempotent: true,
          }
        : null,
  );

export interface MarkIncompleteInput {
  sku: string;
  qtyPending: number;
  expectedDate?: string;
}

export const useMarkRxIncomplete = () =>
  useRxAction<MarkIncompleteInput>(
    ({ id }) => `/rx/prescriptions/${id}/mark_incomplete`,
    ({ id, sku, qtyPending, expectedDate }) => ({
      path: `/api/prescriptions/${id}/mark_incomplete/`,
      body: {
        sku,
        qty_pending: qtyPending,
        expected_delivery_date: expectedDate ? isoDate(expectedDate) : null,
      },
    }),
    ({ expectedDate }) => ({ expectedDate }),
  );

export const useCollectOwing = () =>
  useRxAction<object>(
    ({ id }) => `/rx/prescriptions/${id}/status`,
    ({ id }) => ({ path: `/api/prescriptions/${id}/collect_owing/` }),
    () => ({ status: 'Fulfilled', note: 'Owing items collected' }),
  );

/** Formalise an owing — a partial supply has left; generates the owing note (§9, PH only). */
export const useCreateOwing = () =>
  useRxAction<object>(
    ({ id }) => `/rx/prescriptions/${id}/status`,
    ({ id }) => ({ path: `/api/prescriptions/${id}/create_owing/` }),
    () => ({ status: 'Owing', note: 'Owing note generated' }),
  );

export const useGenerateRxTicket = () =>
  useRxAction<object>(({ id }) => `/rx/prescriptions/${id}/ticket`);

export interface TokenPrintInput {
  reprint?: boolean;
  reason?: string;
  terminal?: string;
}

export const usePrintRxToken = () =>
  useRxAction<TokenPrintInput>(
    ({ id }) => `/rx/prescriptions/${id}/print_token`,
    ({ id, reprint, reason, terminal }) => ({
      path: `/api/prescriptions/${id}/print_token/`,
      body: { reprint: !!reprint, reason: reason ?? '', terminal: terminal ?? '' },
    }),
  );

export interface ManualRxInput {
  patient: string;
  nhs: string;
  dob: string;
  prescriber: string;
  practice: string;
  store: string;
  cd: boolean;
  lines: {
    drug: string;
    sku: string;
    qty: number;
    dosage?: string;
    level?: 'vmp' | 'amp' | 'ampp' | 'vmpp' | 'cnd';
  }[];
}

export function useCreateManualRx() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: async (input: ManualRxInput) => {
      if (!useRealApi) {
        return apiFetch<RxPrescription>('/rx/prescriptions', {
          method: 'POST',
          body: { ...input, who: me, role },
        });
      }
      const raw = await apiFetch<unknown>('/api/prescriptions/', {
        method: 'POST',
        body: {
          store_id: input.store,
          nhs_number: input.nhs,
          patient_name: input.patient,
          patient_dob: input.dob ? isoDate(input.dob) : null,
          prescriber: input.prescriber,
          practice: input.practice,
          is_cd: input.cd,
          lines: input.lines.map((l) => ({
            sku: l.sku,
            drug_name: l.drug,
            qty: l.qty,
            ...(l.dosage ? { dosage: l.dosage } : {}),
            ...(l.level ? { level: l.level } : {}),
          })),
        },
      });
      return prescriptionToRx(raw);
    },
    onSuccess: () =>
      RX_INVALIDATE.forEach((key) => qc.invalidateQueries({ queryKey: key as never })),
  });
}

export function useEpsFetch() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: async (nhsNumber?: string) => {
      if (!useRealApi) {
        return apiFetch<RxPrescription>('/rx/eps/fetch', {
          method: 'POST',
          body: { who: me, role },
        });
      }
      return apiFetch<unknown>('/api/prescriptions/eps/fetch/', {
        method: 'POST',
        body: { nhs_number: nhsNumber ?? '9434765919' },
      });
    },
    onSuccess: () =>
      RX_INVALIDATE.forEach((key) => qc.invalidateQueries({ queryKey: key as never })),
  });
}

export function useToggleManualMode() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: async (on: boolean) => {
      if (!useRealApi) {
        return apiFetch<SpineState & { manualMode: boolean }>('/rx/spine/manual-mode', {
          method: 'POST',
          body: { on, who: me, role },
        });
      }
      // No live Spine integration yet — flip the cached panel state locally.
      const current = qc.getQueryData<SpineState & { manualMode: boolean }>(rxKeys.spine());
      const next = { ...(current as SpineState & { manualMode: boolean }), manualMode: on };
      qc.setQueryData(rxKeys.spine(), next);
      return next;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: rxKeys.spine() }),
  });
}
